import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AddToCartModal from './AddToCartModal';
import Button from './Button';

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  src: string;
  alt: string;
  title?: string;
  description?: string;
  imageId?: string;
}

const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  onClose,
  src,
  alt,
  title,
  description,
  imageId,
}) => {
  const { t } = useTranslation();
  const [showAddToCart, setShowAddToCart] = useState(false);

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-dark-primary/95 backdrop-blur-sm"
              onClick={onClose}
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="relative max-w-6xl w-full max-h-[90vh] overflow-y-auto bg-dark-surface border border-dark-border/30"
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 text-cream hover:text-gold transition-colors bg-dark-primary/80 border border-dark-border/30 hover:border-gold/50"
                aria-label={t('modals.close')}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <div className="flex flex-col lg:flex-row">
                {/* Image Section */}
                <div className="lg:w-2/3 p-8 flex items-center justify-center bg-dark-primary/50">
                  {/* Actual Image */}
                  <div className="w-full max-w-2xl">
                    <img
                      src={src}
                      alt={alt}
                      className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
                      onError={(e) => {
                        // Fallback to gradient if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = `
                          <div class="w-full aspect-[3/4] bg-gradient-to-br from-dark-surface via-dark-border/30 to-gold/10 flex items-center justify-center rounded-lg">
                            <div class="text-center px-6">
                              <h3 class="font-playfair text-3xl text-cream mb-4">${title || 'Artwork'}</h3>
                              <div class="w-16 h-px bg-gold/50 mx-auto mb-4"></div>
                              <p class="text-cream-dark font-light">${description || 'AI-generated artwork'}</p>
                            </div>
                          </div>
                        `;
                      }}
                    />
                  </div>
                </div>

                {/* Info Section */}
                <div className="lg:w-1/3 p-8 bg-dark-surface">
                  {/* Artwork info */}
                  <div className="mb-8">
                    {title && (
                      <h3 className="font-playfair text-2xl text-cream mb-4">{title}</h3>
                    )}
                    {description && (
                      <p className="text-cream-dark font-light leading-relaxed">{description}</p>
                    )}
                  </div>

                  {/* Elegant separator */}
                  <div className="w-12 h-px bg-gold/30 mb-8"></div>

                  {/* Order button */}
                  <div className="space-y-4">
                    <Button
                      onClick={() => setShowAddToCart(true)}
                      variant="primary"
                      size="lg"
                      className="w-full"
                    >
                      <span className="flex items-center justify-center">
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {t('cart.orderNow')}
                      </span>
                    </Button>

                    {/* View pricing link */}
                    <div className="pt-4 border-t border-dark-border/30 text-center">
                      <a
                        href="#pricing"
                        onClick={(e) => {
                          e.preventDefault();
                          const element = document.getElementById('pricing');
                          if (element) {
                            element.scrollIntoView({
                              behavior: 'smooth',
                              block: 'start',
                            });
                          }
                          onClose();
                        }}
                        className="text-gold hover:text-gold/80 font-light text-sm tracking-wider uppercase transition-colors duration-300"
                      >
                        {t('pricing.viewFullPriceList')} →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add to Cart Modal */}
      {showAddToCart && (
        <AddToCartModal
          isOpen={showAddToCart}
          onClose={() => setShowAddToCart(false)}
          imageId={imageId || `image-${Date.now()}`}
          imageSrc={src}
          imageTitle={title || alt}
          imageDescription={description}
        />
      )}
    </>
  );
};

export default ImageModal;